/*
  Search results pagination repair (search page only).

  Shopify's search index can report more matches in `search.results_count`
  than `search.results` really returns (usually for a while after bulk product
  changes). `paginate` then serves pages with missing products, which shows up
  as gaps in the grid, and page links that lead to empty pages.

  When, and only when, that happens this walks the pages Shopify will really
  serve, joins up the products that exist and paginates that list client side,
  so every page is full, the page links stop at the last page with products
  and the result count matches what can be browsed.

  A healthy search costs one small extra request (a check of the last page)
  per query, remembered for the session. Nothing is hidden while this runs and
  if any request fails Shopify's own markup is left untouched.

  Based on Mukesh's first version of this fix.
*/
(function () {
  var selectors = {
    meta: '[data-search-pagination]',
    grid: '#AEX-Page-Loop',
    pagination: '#AEX-NonPagination',
    ajaxContent: '#CollectionAjaxContent',
    resultsCount: '[data-search-resultscount]'
  };

  var cachePrefix = 'aex-search-pagination:';
  var cacheTtl = 30 * 60 * 1000;
  // Shopify caps search at 1000 results, so this never cuts a result list short.
  var maxPageFetches = 40;
  var batchSize = 3;
  var loadingClass = 'search-grid--loading';

  var chevron =
    '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" class="icon icon--wide icon-chevron-down"><path d="M14.625 6.75L9 12.375L3.375 6.75" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  /* ------------------------------------------------------------------ cache */

  function readCache(key) {
    try {
      var data = JSON.parse(sessionStorage.getItem(key));
      if (!data || Date.now() - data.ts > cacheTtl) return null;

      return data;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, data) {
    try {
      data.ts = Date.now();
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      // Private browsing or a full quota, everything still works without it.
    }
  }

  /* ------------------------------------------------------------------ fetch */

  function baseParams() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    return params;
  }

  function pageUrl(page) {
    var params = baseParams();
    params.set('page', page);
    return window.location.pathname + '?' + params.toString();
  }

  function cacheKey() {
    return cachePrefix + baseParams().toString();
  }

  function itemHandle(el) {
    if (el.getAttribute('data-product-handle')) {
      return el.getAttribute('data-product-handle');
    }

    var child = el.querySelector('[data-product-handle]');
    return child ? child.getAttribute('data-product-handle') : null;
  }

  /*
    Results the grid does not render (articles, pages, collections) still take
    up a slot in Shopify's page, so they are not missing products.
  */
  function skippedCount(doc) {
    var meta = doc.querySelector(selectors.meta);
    return (meta && parseInt(meta.getAttribute('data-skipped'), 10)) || 0;
  }

  /*
    Rejects when the response carries no grid at all, which means the request
    failed (or was challenged) rather than the page being past the end.
  */
  function fetchPageItems(sectionId, page) {
    var params = baseParams();
    params.set('section_id', sectionId);
    params.set('page', page);

    return fetch(window.location.pathname + '?' + params.toString())
      .then(function (response) {
        if (!response.ok) throw new Error('Search page ' + page + ' failed');
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid = doc.querySelector(selectors.grid);

        // A page past the end still renders the grid, just with nothing in it.
        if (!grid) throw new Error('Search page ' + page + ' could not be read');

        var items = Array.prototype.slice.call(grid.children);
        items.skipped = skippedCount(doc);
        return items;
      });
  }

  /*
    Collects every product Shopify will actually serve for this query, in page
    order, stopping at the first page that comes back empty.
  */
  function collectItems(context) {
    var items = [];
    var seen = {};
    var result = { items: items, sizes: [], duplicates: 0 };
    var lastPage = Math.min(context.serverPages, maxPageFetches);

    function add(pageItems) {
      pageItems.forEach(function (el) {
        var handle = itemHandle(el);
        if (handle) {
          if (seen[handle]) {
            result.duplicates++;
            return;
          }
          seen[handle] = true;
        }

        items.push(el);
      });

      result.sizes.push(pageItems.length);
    }

    function step(page) {
      if (page > lastPage) return Promise.resolve(result);

      var batch = [];
      for (var next = page; next < page + batchSize && next <= lastPage; next++) {
        batch.push(
          next === context.currentPage && context.currentItems.length
            ? Promise.resolve(context.currentItems)
            : fetchPageItems(context.sectionId, next)
        );
      }

      return Promise.all(batch).then(function (pages) {
        var exhausted = false;

        pages.forEach(function (pageItems) {
          if (exhausted) return;

          if (!pageItems.length && !pageItems.skipped) {
            exhausted = true;
            return;
          }

          add(pageItems);
        });

        return exhausted ? result : step(page + batch.length);
      });
    }

    return step(1);
  }

  /* ----------------------------------------------------------------- render */

  function renderItems(grid, items) {
    grid.innerHTML = '';

    items.forEach(function (el) {
      grid.appendChild(document.importNode(el, true));
    });
  }

  function sameItems(grid, items) {
    if (grid.children.length !== items.length) return false;

    return items.every(function (el, index) {
      return itemHandle(el) === itemHandle(grid.children[index]);
    });
  }

  function pageNumbers(pages, currentPage) {
    var shown = [];
    var page;

    if (pages <= 7) {
      for (page = 1; page <= pages; page++) shown.push(page);
      return shown;
    }

    shown = [1, pages, currentPage];
    if (currentPage - 1 > 1) shown.push(currentPage - 1);
    if (currentPage + 1 < pages) shown.push(currentPage + 1);

    return shown
      .filter(function (value, index, list) {
        return list.indexOf(value) === index;
      })
      .sort(function (a, b) {
        return a - b;
      });
  }

  function renderPagination(pages, currentPage) {
    var container = document.querySelector(selectors.pagination);
    if (!container) return;

    if (pages <= 1) {
      container.innerHTML = '';
      return;
    }

    var html = '<div class="pagination">';

    if (currentPage > 1) {
      html +=
        '<span class="prev"><a href="' +
        pageUrl(currentPage - 1) +
        '" style="rotate: 90deg">' +
        chevron +
        '</a></span>';
    }

    var previous = 0;

    pageNumbers(pages, currentPage).forEach(function (page) {
      if (page - previous > 1) html += '<span class="page">&hellip;</span>';
      previous = page;

      html +=
        page === currentPage
          ? '<span class="page current">' + page + '</span>'
          : '<span class="page"><a href="' +
            pageUrl(page) +
            '">' +
            page +
            '</a></span>';
    });

    if (currentPage < pages) {
      html +=
        '<span class="next"><a href="' +
        pageUrl(currentPage + 1) +
        '" style="rotate: 270deg">' +
        chevron +
        '</a></span>';
    }

    container.innerHTML = html + '</div>';
  }

  function updateResultsCount(total) {
    var el = document.querySelector(selectors.resultsCount);
    if (!el) return;

    var reported = el.getAttribute('data-search-resultscount');
    if (!reported || parseInt(reported, 10) === total) return;

    el.textContent = el.textContent.replace(
      new RegExp('\\b' + reported + '\\b'),
      total
    );
    el.setAttribute('data-search-resultscount', total);
  }

  function afterRender() {
    try {
      if (window.theme && theme.reinitProductGridItem) {
        theme.reinitProductGridItem();
      }
    } catch (e) {
      // Grid items still render, they just miss quick shop rebinding.
    }

    try {
      DoublyGlobalCurrency.convertAll(DoublyGlobalCurrency.currentCurrency, '');
    } catch (e) {
      // Currency app is not always present.
    }
  }

  /* ------------------------------------------------------- search page work */

  function show(context, slice, pages, page, total) {
    if (!sameItems(context.grid, slice)) {
      renderItems(context.grid, slice);
      afterRender();
    }

    renderPagination(pages, page);
    updateResultsCount(total);

    if (page !== context.currentPage) {
      history.replaceState({}, '', pageUrl(page));
    }
  }

  function repair(context) {
    var grid = context.grid;
    grid.classList.add(loadingClass);

    return collectItems(context)
      .then(function (result) {
        var items = result.items;

        grid.classList.remove(loadingClass);
        if (!items.length) return;

        var pages = Math.ceil(items.length / context.pageSize);
        var page = Math.min(context.currentPage, pages);

        writeCache(cacheKey(), {
          pages: pages,
          total: items.length,
          sizes: result.duplicates ? null : result.sizes
        });

        show(
          context,
          items.slice((page - 1) * context.pageSize, page * context.pageSize),
          pages,
          page,
          items.length
        );
      })
      .catch(function () {
        // A page could not be read, so leave Shopify's own markup in place.
        grid.classList.remove(loadingClass);
      });
  }

  /*
    Once a query has been swept, the number of products on each server page is
    known, so another page of it only needs the one to three server pages that
    hold its products. Falls back to a full sweep if the results have shifted.
  */
  function repairFromCache(context, cached) {
    var page = Math.min(context.currentPage, cached.pages);
    var from = (page - 1) * context.pageSize;
    var to = Math.min(page * context.pageSize, cached.total);
    var needed = [];
    var offset = 0;
    var skip = 0;

    cached.sizes.forEach(function (size, index) {
      if (offset < to && offset + size > from) {
        if (!needed.length) skip = from - offset;
        needed.push(index + 1);
      }

      offset += size;
    });

    context.grid.classList.add(loadingClass);

    return Promise.all(
      needed.map(function (serverPage) {
        return serverPage === context.currentPage
          ? context.currentItems
          : fetchPageItems(context.sectionId, serverPage);
      })
    )
      .then(function (pages) {
        var items = [];
        var shifted = pages.some(function (pageItems, index) {
          items = items.concat(pageItems);
          return pageItems.length !== cached.sizes[needed[index] - 1];
        });

        if (shifted) return repair(context);

        context.grid.classList.remove(loadingClass);
        show(
          context,
          items.slice(skip, skip + to - from),
          cached.pages,
          page,
          cached.total
        );
      })
      .catch(function () {
        context.grid.classList.remove(loadingClass);
      });
  }

  function run() {
    var meta = document.querySelector(selectors.meta);
    if (!meta || meta.dataset.searchPaginationDone === 'true') return;

    var grid = document.querySelector(selectors.grid);
    var pagination = document.querySelector(selectors.pagination);

    // Endless scroll / load more modes hide the page links and never show gaps.
    if (!grid || !pagination || pagination.classList.contains('hide')) return;

    var context = {
      grid: grid,
      sectionId: meta.dataset.sectionId,
      pageSize: parseInt(meta.dataset.pageSize, 10),
      currentPage: parseInt(meta.dataset.currentPage, 10),
      serverPages: parseInt(meta.dataset.serverPages, 10),
      currentItems: Array.prototype.slice.call(grid.children),
      skipped: skippedCount(document)
    };

    if (
      !context.sectionId ||
      !context.pageSize ||
      !context.currentPage ||
      !(context.serverPages > 1)
    ) {
      return;
    }

    meta.dataset.searchPaginationDone = 'true';

    // Slots Shopify filled on this page, whether or not the grid renders them.
    var rendered = context.currentItems.length + context.skipped;
    var isLastPage = context.currentPage >= context.serverPages;
    var cached = readCache(cacheKey());

    if (cached && cached.pages) {
      var expected = Math.min(
        context.pageSize,
        Math.max(0, cached.total - (context.currentPage - 1) * context.pageSize)
      );

      if (context.currentPage <= cached.pages && rendered === expected) {
        renderPagination(cached.pages, context.currentPage);
        updateResultsCount(cached.total);
        return;
      }

      if (cached.sizes) {
        repairFromCache(context, cached);
        return;
      }
    }

    // A short page that is not the last one, or an empty one, is the bug.
    if (rendered === 0 || (rendered < context.pageSize && !isLastPage)) {
      repair(context);
      return;
    }

    if (isLastPage || (cached && cached.honest)) return;

    /*
      This page is full, but the page links may still point past the end of the
      real results. One check of the last advertised page settles it.
    */
    fetchPageItems(context.sectionId, context.serverPages)
      .then(function (items) {
        if (items.length || items.skipped) {
          writeCache(cacheKey(), { honest: true });
          return;
        }

        return repair(context);
      })
      .catch(function () {
        // Check failed, so leave Shopify's own pagination in place.
      });
  }

  function watchAjaxContent() {
    var content = document.querySelector(selectors.ajaxContent);
    if (!content || !window.MutationObserver) return;

    var scheduled = false;

    new MutationObserver(function () {
      if (scheduled) return;

      scheduled = true;
      window.setTimeout(function () {
        scheduled = false;
        run();
      }, 50);
    }).observe(content, { childList: true, subtree: true });
  }

  function init() {
    if (!document.querySelector(selectors.ajaxContent)) return;

    run();
    watchAjaxContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
